import { env } from "../../../../environments/environment";
import { YourAPPProductType } from "../../shared/contentTypes";
import { CoreLogService } from "../log.service";

/** UAPP Environment IndexedDB Interface
 * [IndexedDB API](https://developer.mozilla.org/de/docs/Web/API/IndexedDB_API)
*/
export interface IIndexedDB {
  get(key: string): Promise<string>;
  add(key: string, value: string): Promise<void>;
  put(key: string, value: string): Promise<void>;
  delete(key: string): Promise<void>;
  deleteStartWith(keyDir: string): Promise<void>;
  deleteRange(fromKey: string, toKey: string): Promise<void>;
  clear(): Promise<void>;
}

// todo implimentierung von mehreren tabellen für SingelDataModelService?

/** Standardimplementierung */
export class IndexedDBDefault implements IIndexedDB {
  /** Name der Datenbank in der IndexedDB, unterschiedlich um mehrere Produkte nter der gleichen Domain laufen lassen zu können */
  private get databaseName(): string {
    if (env.producttype === YourAPPProductType.sot) {
      return "sot";
    } else if (env.producttype === YourAPPProductType.app_android || env.producttype === YourAPPProductType.app_ios) {
      return "app";
    } else if (env.producttype === YourAPPProductType.emenu) {
      return "emenu";
    } else {
      return "yourapp"; // webshop
    }
  }

  private readonly dictionaryTableName: string = "dic";
  private readonly dictionaryTableKeyPath: string = "key";
  readonly currentDbVersion: number = 1;
  private _db: IDBDatabase = null;
  private _initDone: boolean = false;

  /** UAPP Environment IndexedDB Interface
   * [IndexedDB API](https://developer.mozilla.org/de/docs/Web/API/IndexedDB_API)
   * @param _coreLogService CoreLogService
   */
  constructor(private _coreLogService: CoreLogService) {

    const me = this;
    // prüfung das es auch im browser läuft und nicht in NodeJS
    if (typeof window !== "undefined") {
      // initialisiere cache, for performance
      const indexedDB = window.indexedDB;
      if (indexedDB) {
        const response = indexedDB.open(me.databaseName, me.currentDbVersion);
        response.onupgradeneeded = (ev: IDBVersionChangeEvent) => {
          me.onupgradeneeded(ev);
        };
        response.onsuccess = (ev: Event) => {
          if (ev.target instanceof IDBOpenDBRequest) {
            me._db = ev.target.result;
            // bind on error
            me._db.onerror = (ev: Event) => {
              me._coreLogService.error("IndexedDBDefault.constructor", "response.onupgradeneeded > db.onerror", ev);
            }
          } else {
            me._coreLogService.error("IndexedDBDefault.constructor", "onsuccess ev.target is not IDBDatabase");
          }
          me._initDone = true;
        };
        response.onerror = (ev: Event) => {
          me._initDone = true;
          if (ev.target instanceof IDBOpenDBRequest) {
            me._coreLogService.error("IndexedDBDefault.constructor", "onerror", ev.target.error);
          } else {
            me._coreLogService.error("IndexedDBDefault.constructor", "onerror", ev);
          }
          // todo try drop database and create again? 
        };
      } else {
        me._coreLogService.error("IndexedDBDefault.constructor", "window.indexedDB is not defined");
        me._initDone = true;
      }
    }
    else {
      // beim Datenabruf unter NodeJS gibt es kein window
      me._initDone = true;
    }
  };

  /**
   * Private funktion to update the Database to right version
   * @param ev Event from indexedDb onupgradeneeded
   */
  private onupgradeneeded(ev: IDBVersionChangeEvent): void {
    const me = this;
    if (me._initDone)
      throw new Error("onupgradeneeded called after init done.");

    if (ev.target instanceof IDBOpenDBRequest) {
      if (me.currentDbVersion != ev.newVersion)
        throw new Error(`New db version ${ev.newVersion} is not ${me.currentDbVersion}.`);

      const db = ev.target.result;
      // bind on error
      db.onerror = (ev: Event) => {
        me._coreLogService.error("IndexedDBDefault.onupgradeneeded", "db.onerror", ev);
      }

      // update to version 1
      if (ev.oldVersion < 1) {
        db.createObjectStore(me.dictionaryTableName, { keyPath: me.dictionaryTableKeyPath });
      }
      // update to version 2
      //if (ev.oldVersion < 2)
      //{
      //  todo
      //}

      // unbind on error
      db.onerror = null;
    } else {
      me._coreLogService.error("IndexedDBDefault.onupgradeneeded", "ev.target is not IDBDatabase");
    }
  }

  /**
   * waits to InitDone for 15 sec. 
   */
  private ensureInitDone(): Promise<void> {
    const me = this;
    if (me._initDone)
      return Promise.resolve();

    return new Promise<void>((resolve, reject) => {
      // start timeout 15 sec. for reject (agains forever loop)
      const timeout = setTimeout(() => {
        me._coreLogService.error("IndexedDBDefault.ensureInitDone", "timeout");
        return reject();
      }, 15000);
      const waitForInitDone = () => {
        if (me._initDone) {
          clearTimeout(timeout); // stop timeout
          return resolve();
        }
        setTimeout(waitForInitDone, 30);
      };
      waitForInitDone();
    });
  }

  /**
   * Gets the Value for object (undefined, if no entry found)
   * @param key Key of Object
   */
  public get(key: string): Promise<string> {
    const me = this;
    return new Promise<string>((resolve, reject) => {
      me.ensureInitDone().then(() => {
        var transaction = me._db.transaction(me.dictionaryTableName, "readonly");
        var objectStore = transaction.objectStore(me.dictionaryTableName);
        var request = objectStore.get(key);
        request.onerror = (ev) => {
          me._coreLogService.error("IndexedDBDefault.get", "request.onerror", request.error);
          reject(request.error);
        };
        request.onsuccess = (ev) => {
          if (request.result)
            resolve(request.result.value);
          else
            resolve(undefined);
        };
      });
    });
  }

  /**
   * Inserts a new key value pair (cant update)
   * @param key key of value
   * @param value value for key
   */
  public add(key: string, value: string): Promise<void> {
    const me = this;
    return new Promise<void>((resolve, reject) => {
      me.ensureInitDone().then(() => {
        var transaction = me._db.transaction(me.dictionaryTableName, "readwrite");
        var objectStore = transaction.objectStore(me.dictionaryTableName);
        var obj = { value: value };
        obj[me.dictionaryTableKeyPath] = key;
        var request = objectStore.add(obj);
        request.onerror = (ev) => {
          me._coreLogService.error("IndexedDBDefault.add", "request.onerror", request.error);
          reject(request.error);
        };
        request.onsuccess = (ev) => {
          resolve();
        };
      });
    });
  }

  /**
   * Updates the value of a existing key or inserts it, if the key dose not exists 
   * @param key key of value
   * @param value value for key
   */
  public put(key: string, value: string): Promise<void> {
    const me = this;
    return new Promise<void>((resolve, reject) => {
      me.ensureInitDone().then(() => {
        var transaction = me._db.transaction(me.dictionaryTableName, "readwrite");
        var objectStore = transaction.objectStore(me.dictionaryTableName);
        var obj = { value: value };
        obj[me.dictionaryTableKeyPath] = key;
        var request = objectStore.put(obj);
        request.onerror = (ev) => {
          me._coreLogService.error("IndexedDBDefault.put", "request.onerror", request.error);
          reject(request.error);
        };
        request.onsuccess = (ev) => {
          resolve();
        };
      });
    });
  }

  /**
   * Removes key value pair by key
   * @param key key of value
   */
  public delete(key: string): Promise<void> {
    const me = this;
    return new Promise<void>((resolve, reject) => {
      me.ensureInitDone().then(() => {
        var transaction = me._db.transaction(me.dictionaryTableName, "readwrite");
        var objectStore = transaction.objectStore(me.dictionaryTableName);
        var request = objectStore.delete(key);
        request.onerror = (ev) => {
          me._coreLogService.error("IndexedDBDefault.delete", "request.onerror", request.error);
          reject(request.error);
        };
        request.onsuccess = (ev) => {
          resolve();
        };
      });
    });
  }

  /**
   * Removes all keys with start of 'keyDir/'
   * @param keyDir dir part of key, like 'dir/key'
   */
  public deleteStartWith(keyDir: string): Promise<void> {
    const me = this;
    return new Promise<void>((resolve, reject) => {
      me.ensureInitDone().then(() => {
        var transaction = me._db.transaction(me.dictionaryTableName, "readwrite");
        var objectStore = transaction.objectStore(me.dictionaryTableName);
        // von '/' (ascii 47) bis '0' (ascii 48) (und die '0' ausschliessen)
        var request = objectStore.delete(IDBKeyRange.bound(keyDir + "/", keyDir + "0", false, true));
        request.onerror = (ev) => {
          me._coreLogService.error("IndexedDBDefault.deleteStartWith", "request.onerror", keyDir, request.error);
          reject(request.error);
        };
        request.onsuccess = (ev) => {
          resolve();
        };
      });
    });
  }

  /**
   * Removes all keys in range like 'product_00000_' - 'product_99999_' > delets all products and sortiments
   * @param fromKey start of key, like 'product_00000_' (fromKey including)
   * @param toKey last start of key, like 'product_99999_' (toKey including)
   */
  public deleteRange(fromKey: string, toKey: string): Promise<void> {
    const me = this;
    return new Promise<void>((resolve, reject) => {
      me.ensureInitDone().then(() => {
        var transaction = me._db.transaction(me.dictionaryTableName, "readwrite");
        var objectStore = transaction.objectStore(me.dictionaryTableName);
        var request = objectStore.delete(IDBKeyRange.bound(fromKey, toKey));
        request.onerror = (ev) => {
          me._coreLogService.error("IndexedDBDefault.deleteRange", "request.onerror", fromKey, toKey, request.error);
          reject(request.error);
        };
        request.onsuccess = (ev) => {
          resolve();
        };
      });
    });
  }

  /**
   * cleans up full dictionary
   */
  public clear(): Promise<void> {
    const me = this;
    return new Promise<void>((resolve, reject) => {
      me.ensureInitDone().then(() => {
        var transaction = me._db.transaction(me.dictionaryTableName, "readwrite");
        var objectStore = transaction.objectStore(me.dictionaryTableName);
        var request = objectStore.clear();
        request.onerror = (ev) => {
          me._coreLogService.error("IndexedDBDefault.clear", "request.onerror", request.error);
          reject(request.error);
        };
        request.onsuccess = (ev) => {
          resolve();
        };
      });
    });
  }
}
